import { useEffect, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { X, MessageSquare, Check, RotateCcw, Trash2, Pencil, Reply } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";

const anchorsOf = (root, id) => (root ? [...root.querySelectorAll(`.cmt[data-cid="${id}"]`)] : []);

export const unwrapComment = (root, id) => anchorsOf(root, id).forEach((a) => a.replaceWith(...a.childNodes));

const Card = ({ c, root, active, onFocus, update, remove, author }) => {
  const [editing, setEditing] = useState(!c.text);
  const [draft, setDraft] = useState(c.text);
  const [reply, setReply] = useState("");
  const anchors = anchorsOf(root, c.id);
  const quote = anchors.map((a) => a.textContent).join(" ").replace(/\u200B/g, "").trim();
  const save = () => { if (!draft.trim()) return; update(c.text ? { text: draft.trim() } : { text: draft.trim(), author, createdAt: Date.now() }); setEditing(false); };
  return (
    <div onClick={onFocus} data-testid={`comment-card-${c.id}`}
      className={`rounded-lg border bg-card p-3 space-y-2 cursor-pointer transition-shadow ${active ? "ring-2 ring-amber-400/70 shadow-sm" : "hover:shadow-sm"} ${c.resolved ? "opacity-60" : ""}`}>
      <div className="text-[11px] text-muted-foreground border-l-2 border-amber-400 pl-2 italic line-clamp-2" data-testid={`comment-quote-${c.id}`}>{quote ? `“${quote}”` : "Commented text was removed"}</div>
      <div className="flex items-center gap-2">
        <span className="h-6 w-6 rounded-full bg-primary/10 text-primary text-[11px] font-semibold flex items-center justify-center">{(c.author || "?")[0].toUpperCase()}</span>
        <span className="text-xs font-semibold">{c.author}</span>
        <span className="text-[11px] text-muted-foreground">{formatDistanceToNow(c.createdAt, { addSuffix: true })}</span>
        {c.resolved && <span className="ml-auto text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Resolved</span>}
      </div>
      {editing ? (
        <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
          <Textarea autoFocus rows={3} value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Write a comment…" data-testid={`comment-input-${c.id}`}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) save(); }} />
          <div className="flex justify-end gap-1.5">
            <Button size="sm" variant="ghost" onClick={() => (c.text ? setEditing(false) : remove())} data-testid={`comment-cancel-${c.id}`}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={!draft.trim()} data-testid={`comment-save-${c.id}`}>Comment</Button>
          </div>
        </div>
      ) : (
        <p className="text-sm whitespace-pre-wrap" data-testid={`comment-text-${c.id}`}>{c.text}</p>
      )}
      {(c.replies || []).map((r, i) => (
        <div key={i} className="ml-4 pl-2 border-l text-sm" data-testid={`comment-reply-${c.id}-${i}`}>
          <span className="text-xs font-semibold mr-1.5">{r.author}</span><span className="whitespace-pre-wrap">{r.text}</span>
        </div>
      ))}
      {active && !editing && (
        <div className="space-y-2 pt-1" onClick={(e) => e.stopPropagation()}>
          <div className="flex gap-1.5">
            <Input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply…" className="h-8 text-sm" data-testid={`comment-reply-input-${c.id}`}
              onKeyDown={(e) => { if (e.key === "Enter" && reply.trim()) { update({ replies: [...(c.replies || []), { author, text: reply.trim(), createdAt: Date.now() }] }); setReply(""); } }} />
            <Button size="icon" variant="outline" className="h-8 w-8 shrink-0" disabled={!reply.trim()} data-testid={`comment-reply-btn-${c.id}`}
              onClick={() => { update({ replies: [...(c.replies || []), { author, text: reply.trim(), createdAt: Date.now() }] }); setReply(""); }}><Reply className="h-3.5 w-3.5" /></Button>
          </div>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => update({ resolved: !c.resolved })} data-testid={`comment-resolve-${c.id}`}>
              {c.resolved ? <><RotateCcw className="h-3.5 w-3.5 mr-1" />Reopen</> : <><Check className="h-3.5 w-3.5 mr-1" />Resolve</>}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setDraft(c.text); setEditing(true); }} data-testid={`comment-edit-${c.id}`}><Pencil className="h-3.5 w-3.5 mr-1" />Edit</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs text-destructive ml-auto" onClick={remove} data-testid={`comment-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5 mr-1" />Delete</Button>
          </div>
        </div>
      )}
    </div>
  );
};

export const CommentsPanel = ({ rootRef, comments, setComments, activeId, setActiveId, onClose, author, setAuthor }) => {
  const [, force] = useState(0);
  const [showResolved, setShowResolved] = useState(true);
  const root = rootRef.current;

  useEffect(() => {
    const el = rootRef.current;
    const h = () => force((n) => n + 1);
    el.addEventListener("input", h);
    return () => el.removeEventListener("input", h);
  }, [rootRef]);

  useEffect(() => {
    root?.querySelectorAll(".cmt").forEach((a) => {
      const c = comments.find((x) => x.id === a.dataset.cid);
      a.classList.toggle("cmt-active", a.dataset.cid === activeId);
      a.classList.toggle("cmt-resolved", !!c?.resolved);
    });
  });

  const pos = (c) => anchorsOf(root, c.id)[0];
  const ordered = [...comments].sort((a, b) => {
    const pa = pos(a), pb = pos(b);
    if (!pa || !pb) return pa ? -1 : pb ? 1 : a.createdAt - b.createdAt;
    return pa.compareDocumentPosition(pb) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
  }).filter((c) => showResolved || !c.resolved);
  const open = comments.filter((c) => !c.resolved).length;

  const update = (id, patch) => setComments(comments.map((c) => (c.id === id ? { ...c, ...patch } : c)));
  const remove = (id) => { unwrapComment(root, id); setComments(comments.filter((c) => c.id !== id)); root.dispatchEvent(new Event("input", { bubbles: true })); };
  const focus = (c) => { setActiveId(c.id); pos(c)?.scrollIntoView({ block: "center", behavior: "smooth" }); };

  return (
    <aside className="w-[320px] shrink-0 border-l bg-muted/30 flex flex-col" data-testid="comments-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b bg-card">
        <div className="flex items-center gap-2 font-semibold text-sm"><MessageSquare className="h-4 w-4 text-amber-500" />Comments <span className="text-xs font-normal text-muted-foreground" data-testid="comments-open-count">{open} open</span></div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} data-testid="comments-close-btn"><X className="h-4 w-4" /></Button>
      </div>
      <div className="px-4 py-2.5 border-b bg-card flex items-center gap-2 text-xs">
        <span className="text-muted-foreground">Your name</span>
        <Input value={author} onChange={(e) => setAuthor(e.target.value)} className="h-7 text-xs flex-1" data-testid="comments-author-input" />
        <label className="flex items-center gap-1 text-muted-foreground whitespace-nowrap cursor-pointer">
          <Checkbox checked={showResolved} onCheckedChange={(v) => setShowResolved(!!v)} data-testid="comments-show-resolved" />Resolved
        </label>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2.5" data-testid="comments-list">
        {!ordered.length && <p className="text-sm text-muted-foreground text-center p-6">Select text in the document and choose Review › New comment to add one. Comments are not printed or exported.</p>}
        {ordered.map((c) => (
          <Card key={c.id} c={c} root={root} author={author} active={activeId === c.id} onFocus={() => focus(c)}
            update={(p) => update(c.id, p)} remove={() => remove(c.id)} />
        ))}
      </div>
    </aside>
  );
};
